// NOTE: The contents of this file will only be executed if
// you uncomment its entry in "web/static/js/app.js".

// To use Phoenix channels, the first step is to import Socket
// and connect at the socket path in "lib/my_app/endpoint.ex":
import { Socket } from "phoenix";

export class WebSocketConnectClass {
  static REBEL_EVENT_ROOM_LOBBY = "rebel_event_room:lobby";

  static REBEL_USER_LOBBY = "rebel:user:lobby";

  static REBEL_PROJECT_ROOM_LOBBY = "rebel_project_room:lobby";

  static REBEL_HUB_ROOM_LOBBY = "rebel_hub_room:lobby";

  constructor(endpoint) {
    this.endpoint = endpoint;
    // channelName -> { channel, joinPush, wired }
    this.channelCache = {};
  }

  connect(jwt) {
    this.currentJWT = jwt;
    this.socket = new Socket(this.endpoint, {
      params: {
        token: jwt,
      },
    });

    this.socket.connect();
    return this.socket;
  }

  close() {
    this.socket.disconnect();
    this.channelCache = {};
  }

  /**
   * Returns the one channel object for `channelName`, creating it on first ask.
   *
   * The cache used to be written nowhere, so every caller got a brand new
   * channel object for the same topic: `leaveChannel` could never find one to
   * leave, and two callers wiring the same topic ended up with two independent
   * subscriptions. Now that the object is shared, joining and wiring have to be
   * done through `joinOnce`/`wireOnce` below — see the comments there.
   */
  channel(jwt, channelName) {
    if (!this.socket) {
      this.connect(jwt);
    } else if (this.currentJWT !== jwt) {
      this.close();
      this.connect(jwt);
    }

    let entry = this.channelCache[channelName];
    if (!entry) {
      entry = { channel: this.socket.channel(channelName), joinPush: null, wired: false };
      this.channelCache[channelName] = entry;
    }

    return entry.channel;
  }

  /**
   * Joins a channel at most once and returns the join push.
   *
   * Phoenix raises "tried to join multiple times" on a second `join()` for the
   * same channel, and several call sites are dispatched more than once for the
   * same topic (e.g. `initializeRebelProjectChannel` from both `fetchProjects`
   * and `fetchProjectsOnUpdate`). Returning the original push is not a
   * downgrade: a push replays its stored response to callbacks registered after
   * it settled, and Phoenix reuses this same push object when it rejoins after a
   * dropped socket, so `.receive("ok", …)` still fires on every (re)join.
   */
  joinOnce(channel) {
    const entry = this.channelCache[channel.topic];
    if (!entry) {
      return channel.join();
    }

    if (!entry.joinPush) {
      entry.joinPush = channel.join();
    }

    return entry.joinPush;
  }

  /**
   * Claims the right to register `channel.on(...)` handlers for this channel.
   *
   * Returns `true` the first time it is called for a given channel object and
   * `false` afterwards, so a caller that is dispatched twice for the same topic
   * can bail out instead of registering every handler a second time — a second
   * registration makes each incoming event dispatch twice.
   */
  claimWiring(channel) {
    const entry = this.channelCache[channel.topic];
    if (!entry) {
      return true;
    }

    if (entry.wired) {
      return false;
    }

    entry.wired = true;
    return true;
  }

  leaveChannel(channelName) {
    const entry = this.channelCache[channelName];
    if (entry) {
      entry.channel.leave();
      delete this.channelCache[channelName];
    }
  }

  static makeChannelName(name, id) {
    let channelName = name;
    if (id) {
      channelName += `:${id}`;
    }

    return channelName;
  }
}

export const WebSocketConnect = new WebSocketConnectClass(
  window?.env?.VITE_WS_ENDPOINT || import.meta.env.VITE_WS_ENDPOINT
);

// When you connect, you'll often need to authenticate the client.
// For example, imagine you have an authentication plug, `MyAuth`,
// which authenticates the session and assigns a `:current_user`.
// If the current user exists you can assign the user's token in
// the connection for use in the layout.
//
// In your "web/router.ex":
//
//     pipeline :browser do
//       ...
//       plug MyAuth
//       plug :put_user_token
//     end
//
//     defp put_user_token(conn, _) do
//       if current_user = conn.assigns[:current_user] do
//         token = Phoenix.Token.sign(conn, "user socket", current_user.id)
//         assign(conn, :user_token, token)
//       else
//         conn
//       end
//     end
//
// Now you need to pass this token to JavaScript. You can do so
// inside a script tag in "web/templates/layout/app.html.eex":
//
//     <script>window.userToken = "<%= assigns[:user_token] %>";</script>
//
// You will need to verify the user token in the "connect/2" function
// in "web/channels/user_socket.ex":
//
//     def connect(%{"token" => token}, socket) do
//       # max_age: 1209600 is equivalent to two weeks in seconds
//       case Phoenix.Token.verify(socket, "user socket", token, max_age: 1209600) do
//         {:ok, user_id} ->
//           {:ok, assign(socket, :user, user_id)}
//         {:error, reason} ->
//           :error
//       end
//     end
//
// Finally, pass the token on connect as below. Or remove it
// from connect if you don't care about authentication.

// socket.connect();

// Now that you are connected, you can join channels with a topic:
// const channel = socket.channel('topic:subtopic', {});
// channel.join()
// .receive('ok', (resp) => { console.log('Joined successfully', resp); });
// .receive('error', (resp) => { console.log('Unable to join', resp); });
